import { istanbulDayString } from '@aquabox/shared';
import type { Order, OrderSource, OrderStatus } from '../api/types';

/**
 * Yazdırma kuyruğu yardımcıları (K2c). Saf fonksiyonlar + `localStorage` okuma/yazma.
 *
 * "Basıldı" bilgisi YALNIZ tarayıcıdadır (sunucuda alan yok). Anahtar: `aquabox.printedOrderIds`,
 * biçim: `{ [tenantId]: { [YYYY-MM-DD (Europe/Istanbul)]: string[] } }`. 7 günden eski gün kayıtları
 * her yazımda budanır. Depo yoksa (gizli mod, kota) işaret tutulmaz, kuyruk yine çalışır.
 */

/** Kuyruğa giren kanallar: panelden çekilen siparişler. */
export const PRINT_QUEUE_SOURCES: OrderSource[] = ['HAYAT_PANEL', 'ABANT_PANEL', 'GETIR_SU'];
/** "Açık" durumlar: teslim, kapıda bırakma, iptal ve dış kapanış dışındakiler. */
export const PRINT_QUEUE_STATUSES: OrderStatus[] = ['NEW', 'CONFIRMED', 'ASSIGNED', 'ON_ROUTE'];
/** Toplu fiş ucu 1–50 id kabul eder (`print.controller`). */
export const PRINT_BATCH_MAX = 50;
/** Kuyruk sorgusu sayfa boyutu: sunucu üst sınırı (`pageSchema`, max 200). */
export const PRINT_QUEUE_PAGE_SIZE = 200;

export const PRINTED_KEY = 'aquabox.printedOrderIds';
const RETENTION_DAYS = 7;
const DAY_MS = 86_400_000;

type PrintedStore = Record<string, Record<string, string[]>>;

/*
 * Basıldı durumu için ortak abonelik: aynı ağaçtaki her `usePrintQueue` (rozet, ekran) aynı anda
 * güncellenir. `version` her değişimde artar; `useSyncExternalStore` bunu izler.
 */
let printedVersion = 0;
const printedListeners = new Set<() => void>();

export function getPrintedVersion(): number {
  return printedVersion;
}

/** Abone ol: bu sekmede `markPrinted` ya da başka sekmede depo değişince `listener` çağrılır. */
export function subscribePrinted(listener: () => void): () => void {
  printedListeners.add(listener);
  const onStorage = (e: StorageEvent): void => {
    if (e.key === PRINTED_KEY || e.key === null) {
      printedVersion += 1;
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    printedListeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function notifyPrinted(): void {
  printedVersion += 1;
  for (const listener of printedListeners) listener();
}

/** Rozet metni: sınır aşıldıysa "200+", değilse basılmamış sayısı, sıfırsa hiç gösterilmez. */
export function printBadgeText(unprinted: number, truncated: boolean): string | undefined {
  if (truncated) return `${PRINT_QUEUE_PAGE_SIZE}+`;
  return unprinted > 0 ? String(unprinted) : undefined;
}

/** Yazdırma yetkisi: sunucudaki `/api/print` ile aynı rol kümesi (OWNER, MANAGER). */
export function canPrint(role: string | undefined): boolean {
  return role === 'OWNER' || role === 'MANAGER';
}

export function isPrintQueueOrder(order: Pick<Order, 'source' | 'status'>): boolean {
  return PRINT_QUEUE_SOURCES.includes(order.source) && PRINT_QUEUE_STATUSES.includes(order.status);
}

export function slipPath(orderId: string): string {
  return `/api/print/orders/${encodeURIComponent(orderId)}/slip.pdf`;
}

/** Sıra korunur; tekilleştirme sunucu tarafında da yapılır. */
export function slipsPath(orderIds: string[]): string {
  return `/api/print/orders/slips.pdf?ids=${orderIds.map((id) => encodeURIComponent(id)).join(',')}`;
}

export function manifestPath(tripId: string): string {
  return `/api/print/trips/${encodeURIComponent(tripId)}/manifest.pdf`;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function readStore(): PrintedStore {
  try {
    const raw = localStorage.getItem(PRINTED_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as PrintedStore)
      : {};
  } catch {
    return {};
  }
}

function cutoffDay(now: Date): string {
  return istanbulDayString(new Date(now.getTime() - RETENTION_DAYS * DAY_MS));
}

/** Kiracının son 7 gündeki basılmış sipariş id'leri. Okuma yan etkisizdir. */
export function loadPrintedIds(tenantId: string | null | undefined, now = new Date()): Set<string> {
  const printed = new Set<string>();
  if (!tenantId) return printed;
  const days = readStore()[tenantId];
  if (!days || typeof days !== 'object') return printed;
  const cutoff = cutoffDay(now);
  for (const [day, ids] of Object.entries(days)) {
    if (day < cutoff || !isStringArray(ids)) continue;
    for (const id of ids) printed.add(id);
  }
  return printed;
}

/** Verilen siparişleri bugünün (İstanbul) kaydına ekler; 7 günden eski kayıtları budar. */
export function markPrinted(
  tenantId: string | null | undefined,
  orderIds: string[],
  now = new Date(),
): void {
  if (!tenantId || orderIds.length === 0) return;
  try {
    const store = readStore();
    const cutoff = cutoffDay(now);
    for (const [tid, days] of Object.entries(store)) {
      if (!days || typeof days !== 'object') {
        delete store[tid];
        continue;
      }
      for (const [day, ids] of Object.entries(days)) {
        if (day < cutoff || !isStringArray(ids)) delete days[day];
      }
    }
    const today = istanbulDayString(now);
    const days = store[tenantId] ?? {};
    days[today] = [...new Set([...(days[today] ?? []), ...orderIds])];
    store[tenantId] = days;
    localStorage.setItem(PRINTED_KEY, JSON.stringify(store));
  } catch {
    // Depo yok ya da dolu: işaret bu oturumda tutulmaz; kuyruk yine çalışır.
  }
  notifyPrinted();
}
